import { useState, useCallback } from 'react';
import type { QuotaType } from '../types';

interface QuotaModalState {
  isOpen: boolean;
  quotaType: QuotaType;
  current: number;
  limit: number;
  eventId?: string;
}

/**
 * Hook to intercept QUOTA_EXCEEDED API errors and manage the QuotaLimitModal state.
 *
 * Usage:
 *   const { quotaModal, handleQuotaError, closeQuotaModal } = useQuotaGuard();
 *
 *   // In your catch block:
 *   catch (err) {
 *     if (!handleQuotaError(err)) {
 *       toast.error('Something went wrong');
 *     }
 *   }
 *
 *   // In JSX:
 *   <QuotaLimitModal {...quotaModal} onClose={closeQuotaModal} />
 */
export function useQuotaGuard() {
  const [quotaModal, setQuotaModal] = useState<QuotaModalState>({
    isOpen: false,
    quotaType: 'events',
    current: 0,
    limit: 0,
  });

  const handleQuotaError = useCallback((err: unknown): boolean => {
    const e = err as { response?: { data?: { code?: string; quotaType?: QuotaType; current?: number; limit?: number } }; status?: number };
    const data = e.response?.data;
    if (data?.code === 'QUOTA_EXCEEDED' && data.quotaType) {
      setQuotaModal({
        isOpen: true,
        quotaType: data.quotaType,
        current: data.current ?? 0,
        limit: data.limit ?? 0,
      });
      return true;
    }
    return false;
  }, []);

  const closeQuotaModal = useCallback(() => {
    setQuotaModal((prev) => ({ ...prev, isOpen: false }));
  }, []);

  return { quotaModal, handleQuotaError, closeQuotaModal };
}
